import { ArrowRight, Check } from '@insify/design-system/icons';
import { textXsSemibold } from '@insify/design-system/typography';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import * as styles from './Indicator.module.css';
import { getSortedIndicators } from './helpers';

import type { ProgressState, StepsGroup } from './types';

type IndicatorItemProps = {
  state: ProgressState;
  index: number;
  children: React.ReactNode;
};

export const IndicatorItem = (props: IndicatorItemProps): JSX.Element => {
  const isActive = props.state === 'active';
  const isFinished = props.state === 'finished';

  return (
    <li className={clsx(styles.indicator, styles[props.state])}>
      <div className={clsx(textXsSemibold, styles.status, styles[props.state])}>
        {isFinished ? <Check data-testid='indicator-checkmark' className={styles.checkMark} /> : props.index}
      </div>
      <div className={styles.text}>{props.children}</div>
      {isActive && <ArrowRight data-testid='indicator-arrow' className={styles.arrow} />}
    </li>
  );
};

export type IndicatorProps<Step extends string> = {
  className?: string;
  currentStep: Step;
  // All steps available right now
  steps: readonly Step[];
  stepsGroups: StepsGroup<Step>[];
};

/**
 * @example
 * Given steps: headcount, revenue, coverage, pastClaims, checkout, confirmation,
 * and the following stepProgressions config:
 * [
 *   {label: 'Quote wizard', firstStepOfTheNextGroup: 'coverage'},
 *   {label: 'Shop', firstStepOfTheNextGroup: 'past-claims-confirm'},
 *   {label: 'Bind wizard', firstStepOfTheNextGroup: 'confirmation'},
 * ]
 * this is what would be rendered for each step:
 * headcount
 * [-] Quote wizard
 * [] Shop
 * [] Bind wizard
 * revenue
 * [-] Quote wizard
 * [] Shop
 * [] Bind wizard
 * coverage
 * [x] Quote wizard
 * [-] Shop
 * [] Bind wizard
 * pastClaims
 * [x] Quote wizard
 * [x] Shop
 * [-] Bind wizard
 * checkout
 * [x] Quote wizard
 * [x] Shop
 * [-] Bind wizard
 * confirmation
 * [x] Quote wizard
 * [x] Shop
 * [x] Bind wizard
 */
export function Indicator<Step extends string>({
  className,
  currentStep,
  steps,
  stepsGroups,
}: IndicatorProps<Step>): JSX.Element {
  const [progression, setProgression] = useState(getSortedIndicators(currentStep, steps, stepsGroups));

  useEffect(() => {
    setProgression(getSortedIndicators(currentStep, steps, stepsGroups));
  }, [currentStep, steps]);

  return (
    <div className={clsx(styles.indicators, className)}>
      {progression.map(({ label, status }, index) => (
        <IndicatorItem key={index} state={status} index={index + 1}>
          {label}
        </IndicatorItem>
      ))}
    </div>
  );
}
